import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, FormGroup, Label, Input, Button, Col, Row } from "reactstrap";
import { Formik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { toast } from "react-toastify";

import apiService from "../../services/apiService";

const UserForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname.includes("user-edit");
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [sites, setSites] = useState([]);
  const [roleCondition, setroleCondition] = useState();

  const [initialValues, setInitialValues] = useState({
    "firstName": "",
    "middleName": "",
    "lastName": "",
    "phoneNumber": "",
    "email": "",
    "role": "",
    "password": "",
    "clusterId": "",
    "regionId": "",
    "siteIds": []
  });

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#38C793" // Green background for selected option
        : state.isFocused
          ? "#E6F9F0" // Lighter green background on hover
          : "white",

      cursor: "pointer",
      menu: (provided) => ({
        ...provided,
        maxHeight: "350px",
        overflowY: "auto",

        borderRadius: '0.5rem',
      }),

    }),

    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px", // Adjust the height of the dropdown menu
    }),

    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#38C793" : "#ced4da", // Green border on focus
      boxShadow: state.isFocused ? "0 0 0 1px #38C793" : null,
      "&:hover": {
        borderColor: "#38C793", // Green border on hover
      },
    }),
    singleValue: (provided) => ({
      ...provided,
    }),
  };

  const fetchUser = async () => {
    try {
      const user = await apiService.get(`user/show/${id}`);

      let userData = {
        ...initialValues,
        firstName: user?.firstName || "",
        email: user?.email || "",
        phoneNumber: user?.phoneNumber || "",
        role: user?.role
          ? {
            label: user.role.name,
            value: user.role.role,
            _id: user.role._id,
          }
          : null,
      }
      if (user?.role?.role === 'cluster_manager' || user?.role?.role === 'hospital_manager') {
        userData = { ...userData, regionId: { label: user.region.name, value: user.region._id }, clusterId: { label: user.cluster.name, value: user.cluster._id } };
        fetchRegions();
        fetchClusters({ value: user?.region_id });
      };
      if (user?.role?.role === 'hospital_manager') {
        userData = { ...userData, siteIds: { ...user.sites[0], label: user.sites[0].name, value: user.sites[0]._id } };
        fetchSites({ value: user?.cluster_id });
      }
      setroleCondition(user?.role)
      setInitialValues(userData);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchUser();
    }
  }, [isEdit]);
  const getValidationSchema = (isEdit, values) => {
    // Log role value to see what's being passed


    return Yup.object().shape({
      firstName: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phoneNumber: Yup.string().required("Phone number is required"),
      role: Yup.object().required("Role is required"),
      ...(values?.role?.name === "Cluster_Manager" && {
        regionId: Yup.object().required("Region is required"),
        clusterId: Yup.object().required("Cluster is required"),
      }),
      ...(values?.role?.name === "Hospital Manager" && {
        regionId: Yup.object().required("Region is required"),
        clusterId: Yup.object().required("Cluster is required"),
        siteIds: Yup.object().required("Site is required"),
      }),
      ...(values?.role?.name === "Regional_Manager" && {

        siteIds: Yup.object().required("Site is required"),
      }),
      ...(isEdit
        ? {} // No validation for password when editing
        : {
          password: Yup.string().required("Password is required"),
        }),


    });
  };

  const handleSubmit = async (values) => {
    const formattedValues = {
      ...values,
      role: values.role.value,
      clusterId: values?.clusterId?.value,
      regionId: values?.regionId?.value,
      siteIds: [values?.siteIds?.value],
    };

    if (isEdit) {

      try {
        if (!formattedValues.password) {
          delete formattedValues.password;
        }
        const response = await apiService.post("user/update", {
          id,
          ...formattedValues,
        });
        if (response) {

          toast.success(response?.message || "User updated successfully!", { toastId: "8" });
          navigate("/users");
        }
      } catch (e) {

        toast.error(e.response?.data?.message || "Failed to update user.", { toastId: "9" });
      }
    } else {
      try {
        const response = await apiService.post("user/create", formattedValues);
        if (response) {
          toast.success(response?.message || "User created successfully!", { toastId: "9" });
          navigate("/users");
        }
      } catch (e) {
        console.log("Error during user creation:", e);
        toast.error(e.response?.data?.message || "Failed to create user.", { toastId: "10" });
      }
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService.get(`roles`);

      if (response?.length) {

        setRoles(
          response
            .filter((item) => item.role !== 'physician_management') // Exclude 'physician_management'
            .map((item) => ({
              ...item,
              label: item.name == "Cluster_Manager" ? "Cluster Manager" : item.name,
              value: item.role,
            }))
        );
      } else {
        setRoles([]);
      }
    } catch (e) {
      console.log("Error fetching users:", e);
    }
  };

  const fetchRegions = async () => {
    try {
      const { data } = await apiService.get(`region`);
      setRegions(data.map(({ name, _id, ...rest }) => ({ label: name, value: _id, ...rest })));
    } catch (e) {
      console.log("Error fetching regions:", e);
    }
  };

  const fetchClusters = async (regionId) => {
    try {
      const params = { region_id: regionId?.value };
      const { data } = await apiService.get(`cluster`, params);
      setClusters(data.map(({ name, _id, ...rest }) => ({ label: name, value: _id, ...rest })));
    } catch (e) {
      console.log("Error fetching regions:", e);
    }
  };

  const fetchSites = async (clusterId) => {
    try {
      const params = { cluster_id: clusterId?.value };
      const { data } = await apiService.get(`site`, params);
      setSites(data.map(({ name, _id, ...rest }) => ({ label: name, value: _id, ...rest })));
    } catch (e) {
      console.log("Error fetching regions:", e);
    }
  }




  useEffect(() => {

    if (roleCondition && roleCondition) {
      fetchRegions();
      fetchClusters(roleCondition?._id);
    }
  }, [roleCondition]);


  useEffect(() => {
    fetchRoles();
  }, [initialValues]);

  return (
    <>
      {" "}
      <div className="mt-5 px-16">
        {/* <h3>{isEdit ? "Edit User" : "Add User"}</h3> */}
        <Formik
          enableReinitialize
          initialValues={initialValues}


          validationSchema={Yup.lazy((values) => getValidationSchema(isEdit, values))}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                {/* Name Field */}
                <Col md={6}>
                  <FormGroup>
                    <Label for="firstName">Name *</Label>
                    <Input
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      className={
                        errors.firstName && touched.firstName
                          ? "is-invalid"
                          : ""
                      }
                      placeholder="Name"
                    />
                    {errors.firstName && touched.firstName && (
                      <div className="invalid-feedback">{errors.firstName}</div>
                    )}
                  </FormGroup>
                </Col>

                {/* Email Field */}
                <Col md={6}>
                  <FormGroup>
                    <Label for="email">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={values.email || ""}
                      onChange={handleChange}
                      className={
                        errors.email && touched.email ? "is-invalid" : ""
                      }
                      placeholder="email"
                    />
                    {errors.email && touched.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                {/* Password Field */}

                {isEdit === false && <Col md={6}>
                  <FormGroup>
                    <Label for="password">Password *</Label>
                    <Input
                      name="password"
                      type="password"
                      value={values.password || ""}
                      onChange={handleChange}
                      className={
                        errors.password && touched.password ? "is-invalid" : ""
                      }
                      placeholder="Password"
                    />
                    {errors.password && touched.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </FormGroup>
                </Col>}


                <Col md={6}>
                  <FormGroup>
                    <Label for="phoneNumber">Phone Number *</Label>
                    <Input
                      name="phoneNumber"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      className={
                        errors.phoneNumber && touched.phoneNumber
                          ? "is-invalid"
                          : ""
                      }
                      placeholder="Phone Number"
                    />
                    {errors.phoneNumber && touched.phoneNumber && (
                      <div className="invalid-feedback">
                        {errors.phoneNumber}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                {/* Role Field */}
                {roleCondition?.role !== "physician_management" && (
                  <Col md={6}>
                    <FormGroup>
                      <Label for="role">Role *</Label>
                      <Select
                        name="role"
                        options={roles}
                        isSearchable={false}
                        value={
                          roles?.find((item) => item._id === values.role?._id) ||
                          null
                        }
                        styles={customStyles}
                        onChange={(selected) => {
                          if (selected.role === 'hospital_manager') {
                            fetchRegions();
                          }
                          if (selected.role === 'cluster_manager') {
                            fetchRegions();
                          }
                          setFieldValue("role", selected)
                        }}
                        className={
                          errors.role && touched.role ? "is-invalid" : ""
                        }
                      />
                      {errors.role && touched.role && (
                        <div className="invalid-feedback d-block">
                          {errors.role}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                )}
                {(isEdit == false &&
                  <>
                    {(values?.role?.role === "hospital_manager" || values?.role?.role === "cluster_manager") && (
                      <>

                        <Col md={6}>
                          <FormGroup>
                            <Label for="hospital">Region *</Label>
                            <Select
                              name="regionId"
                              options={regions}
                              value={values.regionId}
                              onChange={(selected) => {
                                setFieldValue("cluster", null);
                                fetchClusters(selected);
                                setFieldValue("regionId", selected);
                                setFieldValue("clusterId", "");
                                setFieldValue("siteIds", "")
                              }}
                              styles={customStyles}
                              className={
                                errors.regionId && touched.regionId ? "is-invalid" : ""
                              }
                            />
                            {errors.regionId && touched.regionId && (
                              <div className="invalid-feedback d-block">
                                {errors.regionId}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="hospital">Cluster *</Label>
                            <Select
                              name="cluster"
                              options={clusters}
                              value={values.clusterId}
                              onChange={(selected) => {
                                setFieldValue("site", null);
                                if (values?.role?.role === "hospital_manager") {
                                  fetchSites(selected);
                                }
                                setFieldValue("clusterId", selected);
                                setFieldValue("siteIds", "")
                              }}
                              styles={customStyles}
                              className={
                                errors.clusterId && touched.clusterId ? "is-invalid" : ""
                              }
                            />
                            {errors.clusterId && touched.clusterId && (
                              <div className="invalid-feedback d-block">
                                {errors.clusterId}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                        {values?.role?.role === "hospital_manager" && <Col md={6}>
                          <FormGroup>
                            <Label for="hospital">Hospital *</Label>
                            <Select
                              name="site"
                              options={sites}
                              value={values.siteIds}
                              onChange={(selected) => {
                                fetchSites(selected);
                                setFieldValue("siteIds", selected)
                              }}
                              styles={customStyles}
                              className={
                                errors.siteIds && touched.siteIds ? "is-invalid" : ""
                              }
                            />
                            {errors.siteIds && touched.siteIds && (
                              <div className="invalid-feedback d-block">
                                {errors.siteIds}
                              </div>
                            )}
                          </FormGroup>
                        </Col>}
                      </>
                    )}

                  </>

                )}

                {roleCondition?.role === "hospital_manager" && (
                  <>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="hospital">Region *</Label>
                        <Select
                          name="regionId"
                          options={regions}
                          value={values.regionId}
                          onChange={(selected) => {
                            setFieldValue("clusterId", null); // Reset cluster field
                            fetchClusters(selected); // Fetch clusters based on region
                            setFieldValue("regionId", selected); // Set region
                            setFieldValue("clusterId", "");
                            setFieldValue("siteIds", "");
                          }}
                          styles={customStyles}
                          className={errors.regionId && touched.regionId ? "is-invalid" : ""}
                        />
                        {errors.regionId && touched.regionId && (
                          <div className="invalid-feedback d-block">{errors.regionId}</div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label for="hospital">Cluster *</Label>
                        <Select
                          name="clusterId"
                          options={clusters}
                          value={values.clusterId}
                          onChange={(selected) => {
                            setFieldValue("siteIds", []); // Reset site field
                            fetchSites(selected); // Fetch sites based on cluster
                            setFieldValue("clusterId", selected); // Set cluster
                            setFieldValue("siteIds", "");
                          }}
                          styles={customStyles}
                          className={errors.clusterId && touched.clusterId ? "is-invalid" : ""}
                        />
                        {errors.clusterId && touched.clusterId && (
                          <div className="invalid-feedback d-block">{errors.clusterId}</div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label for="hospital">Hospital *</Label>
                        <Select
                          name="siteIds"
                          options={sites}
                          value={values.siteIds}
                          onChange={(selected) => {
                            setFieldValue("siteIds", selected); // Update site field
                          }}

                          styles={customStyles}
                          className={errors.siteIds && touched.siteIds ? "is-invalid" : ""}
                        />
                        {errors.siteIds && touched.siteIds && (
                          <div className="invalid-feedback d-block">{errors.siteIds}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </>
                )}

                {roleCondition?.role === "cluster_manager" && (
                  <>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="hospital">Region *</Label>
                        <Select
                          name="regionId"
                          options={regions}
                          value={values.regionId}
                          onChange={(selected) => {
                            setFieldValue("clusterId", null); // Reset cluster field
                            fetchClusters(selected); // Fetch clusters for cluster manager
                            setFieldValue("regionId", selected); // Set region
                          }}
                          styles={customStyles}
                          className={errors.regionId && touched.regionId ? "is-invalid" : ""}
                        />
                        {errors.regionId && touched.regionId && (
                          <div className="invalid-feedback d-block">{errors.regionId}</div>
                        )}
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label for="hospital">Cluster *</Label>
                        <Select
                          name="clusterId"
                          options={clusters}
                          value={values.clusterId}
                          onChange={(selected) => {
                            setFieldValue("siteIds", []); // Reset site field
                            // fetchSites(selected); // Fetch sites based on selected cluster
                            setFieldValue("clusterId", selected); // Set cluster
                          }}
                          styles={customStyles}
                          className={errors.clusterId && touched.clusterId ? "is-invalid" : ""}
                        />
                        {errors.clusterId && touched.clusterId && (
                          <div className="invalid-feedback d-block">{errors.clusterId}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </>
                )}







              </Row>

              <Row>
                {/* phoneNumber Field */}

                {/* Region Field */}
                {/* <Col md={6}>
                <FormGroup>
                  <Label for="region">Region *</Label>
                  <Select
                    name="region"
                    options={regions}
                    value={values.region}
                    onChange={(selected) => setFieldValue("region", selected)}
                    className={
                      errors.region && touched.region ? "is-invalid" : ""
                    }
                  />
                  {errors.region && touched.region && (
                    <div className="invalid-feedback d-block">
                      {errors.region}
                    </div>
                  )}
                </FormGroup>
              </Col> */}
              </Row>

              <Row>
                {/* SME User Switch */}
                {/* <Col md={12}>
                <FormGroup className="d-flex align-items-center">
                  <Label for="smeUser" className="me-2">
                    SME User?
                  </Label>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      id="smeUser"
                      name="smeUser"
                      checked={values.smeUser}
                      onChange={(e) =>
                        setFieldValue("smeUser", e.target.checked)
                      }
                      className="form-check-input"
                    />
                  </div>
                </FormGroup>
              </Col> */}
              </Row>

              <div className="d-flex justify-content-end mt-[200px]">
                <Button color="success" type="submit">
                  {isEdit ? "Update" : "Submit"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>{" "}
    </>
  );
};

export default UserForm;


// {
//   values?.role?.role === "regional_manager" && (

//     <>

//       <Col md={6}>
//         <FormGroup>
//           <Label for="hospital">Region *</Label>
//           <Select
//             name="siteIds"
//             options={sitesforCluster}
//             value={values.siteIds}
//             onChange={(selected) => {
//               setFieldValue("cluster", null);

//               setFieldValue("siteIds", selected);
//             }}
//             styles={customStyles}
//             className={
//               errors.siteIds && touched.siteIds ? "is-invalid" : ""
//             }
//           />
//           {errors.siteIds && touched.siteIds && (
//             <div className="invalid-feedback d-block">
//               {errors.siteIds}
//             </div>
//           )}

//         </FormGroup>
//       </Col>
//     </>)
// }